import LinkButton from '@carvana/ds-link-button';
import { icon } from '@carvana/ds-styles';
import { Clickable } from '@carvana/merch-components';
import breakpoints from '@carvana/merch-tailwind/breakpoints';
import cx from 'classnames';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { appAssetsCdnUrl } from '~/constants/environment';
import { useConfiguration } from '~/context/common/StaticContext';
import optimizeImage, { type Options } from '~/utilities/optimizeImage';
import { PromoBanners } from '../constants';
import {
  useHandleBannerClicks,
  useHandleShowLogic,
  useSendViewedBannerEvent
} from '../hooks';
import { LeftContent, TitleWrapper, Wrapper } from '../PromoBanner.styles';
import { getBannerName } from '../utils';

const banner = PromoBanners.PriceDrop;

const OPTIMIZATION_CONFIG: Options = {
  format: 'auto',
  optimize: 'high',
  quality: 90
};

export default function PriceDrop() {
  const { shouldUseTitleCasing } = useConfiguration();
  const bannerName = getBannerName(banner);
  const { shouldShow, setIsPriceDropDismissed } = useHandleShowLogic();
  const { handleCloseClick, handleShopAllClick } = useHandleBannerClicks(
    setIsPriceDropDismissed
  );
  const sendViewedBannerEvent = useSendViewedBannerEvent();

  useEffect(() => {
    if (!shouldShow) return;

    sendViewedBannerEvent(bannerName);
  }, [bannerName, sendViewedBannerEvent, shouldShow]);

  return shouldShow ? (
    <Wrapper className="bg-canvas-muted">
      <LeftContent className="sm:pr-2xl md:min-h-[176px] lg:pr-4xl">
        <div className="flex flex-col gap-md md:min-w-[280px]">
          <TitleWrapper>
            We’ve lowered prices to give you more savings!
          </TitleWrapper>
        </div>
        <LinkButton
          variant="default"
          size="sm"
          underline={false}
          data-testid="promo-banner-shop-all"
          data-adobe-id="shop-all-price-drops"
          data-analytics-id="Banner"
          data-analytics-attributes={JSON.stringify({
            category: bannerName,
            label: 'Shop All',
            text: shouldUseTitleCasing
              ? 'Shop All Price Drops'
              : 'Shop all Price Drops'
          })}
          onClick={handleShopAllClick}
        >
          {shouldUseTitleCasing
            ? 'Shop All Price Drops'
            : 'Shop all Price Drops'}
        </LinkButton>
      </LeftContent>

      <picture>
        <source
          media={`(min-width: ${breakpoints.lg}px)`}
          srcSet={optimizeImage(
            `${appAssetsCdnUrl}/images/helios/promoBanners/priceDrop/desktop.png`,
            { ...OPTIMIZATION_CONFIG, width: 518 }
          )}
        />
        <source
          media={`(min-width: ${breakpoints.sm}px)`}
          srcSet={optimizeImage(
            `${appAssetsCdnUrl}/images/helios/promoBanners/priceDrop/tablet.png`,
            { ...OPTIMIZATION_CONFIG, width: 352 }
          )}
        />
        <img
          alt="Price Drop Image"
          src={optimizeImage(
            `${appAssetsCdnUrl}/images/helios/promoBanners/priceDrop/mobile.png`,
            { ...OPTIMIZATION_CONFIG, width: 200 }
          )}
          className={cx(
            'absolute right-0 h-full object-cover object-left',
            'sm:w-[352px] lg:w-[440px] xl:w-[518px]'
          )}
        />
      </picture>

      <Clickable
        aria-label="Close banner"
        data-testid="promo-banner-close"
        data-adobe-id="promo-banner-close"
        data-analytics-id={null}
        onClick={handleCloseClick}
        className="absolute right-md top-md flex h-2xl w-2xl items-center justify-center sm:h-3xl sm:w-3xl"
      >
        <X
          aria-hidden
          size={24}
          className="h-xl w-xl sm:h-2xl sm:w-2xl"
          color={icon.default}
        />
      </Clickable>
    </Wrapper>
  ) : null;
}
